import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Volume2 } from "lucide-react";
import { ASSETS, CATS } from "../../data/pusheen";
import { useSound } from "../../sound/SoundContext";

const FLOATERS = [
  { src: CATS.heart, cls: "top-[16%] left-[10%] w-16 sm:w-24 animate-float", d: "0s" },
  { src: CATS.cookie, cls: "top-[22%] right-[12%] w-16 sm:w-24 animate-bob", d: "0.5s" },
  { src: CATS.sleep, cls: "bottom-[16%] left-[8%] w-20 sm:w-28 animate-sway", d: "0.3s" },
  { src: CATS.donut, cls: "bottom-[20%] right-[10%] w-16 sm:w-24 animate-float", d: "0.9s" },
];

export default function IntroGate({ onEnter }) {
  const { play, startMusic } = useSound();

  const handleEnter = () => {
    startMusic();
    play("yay");
    onEnter();
  };

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.04 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
      data-testid="intro-gate"
      className="fixed inset-0 z-[200] bg-pusheen-mint flex flex-col items-center justify-center overflow-hidden px-6"
    >
      {/* soft asset backdrop */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.12] bg-center bg-cover"
        style={{ backgroundImage: `url(${ASSETS.bg})` }}
        aria-hidden="true"
      />

      {/* floating cats */}
      {FLOATERS.map((f, i) => (
        <img
          key={i}
          src={f.src}
          alt=""
          aria-hidden="true"
          className={`pointer-events-none absolute opacity-70 drop-shadow-[3px_4px_0_rgba(74,55,40,0.15)] ${f.cls}`}
          style={{ animationDelay: f.d }}
        />
      ))}

      <div className="relative z-10 flex flex-col items-center text-center">
        <motion.img
          initial={{ opacity: 0, y: 24, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 140, damping: 16 }}
          src={ASSETS.logo}
          alt="Pusheen the Cat"
          className="w-[68%] sm:w-[46%] max-w-sm select-none"
          draggable="false"
        />

        <motion.img
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25, type: "spring", stiffness: 160, damping: 14 }}
          src={CATS.wave}
          alt=""
          aria-hidden="true"
          className="w-24 sm:w-28 animate-bob mt-1 drop-shadow-[5px_6px_0_rgba(74,55,40,0.12)]"
          draggable="false"
        />

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="font-script text-xl sm:text-2xl text-pusheen-brown mt-4"
        >
          welcome to the cutest corner of the internet
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, type: "spring", stiffness: 200, damping: 18 }}
          whileHover={{ y: -3 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleEnter}
          data-testid="intro-enter-button"
          className="group mt-6 bg-pusheen-pink text-pusheen-brown border-[3px] border-pusheen-brown rounded-full px-10 py-3.5 font-display font-extrabold text-2xl shadow-[5px_5px_0_#4A3728] hover:bg-pusheen-pinklight active:translate-y-[5px] active:shadow-none transition-all inline-flex items-center gap-2"
        >
          Enter
          <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
        </motion.button>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="font-body text-sm text-pusheen-brownlight/80 mt-3 inline-flex items-center gap-1.5"
        >
          <Volume2 className="w-4 h-4" /> tap Enter to start the music
        </motion.p>
      </div>
    </motion.div>
  );
}
